import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { 
  AlertTriangle, Phone, Mail, X, ShieldOff, Clock, 
  HelpCircle, MessageCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AccountSuspensionPopup - Shows when a provider's account is suspended
 * Blocks access to job features and shows contact admin message
 */
export default function AccountSuspensionPopup() {
  const { user, getAuthHeaders, logout } = useAuth();
  const [suspensionData, setSuspensionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const checkSuspension = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      // Only check for provider types
      const isProvider = ['mechanic', 'roadside_tech', 'roadside_provider'].includes(user.user_type);
      if (!isProvider) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/user/suspension-status`, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        
        setSuspensionData(response.data);
        
        if (response.data.is_suspended) {
          setShowPopup(true);
        } else if (response.data.is_pending_suspension) {
          // Show warning but don't block
          setShowPopup(false);
        }
      } catch (error) {
        console.error('Failed to check suspension status:', error);
      } finally {
        setLoading(false);
      }
    };

    checkSuspension();
    // Check every 30 seconds
    const interval = setInterval(checkSuspension, 30000);
    return () => clearInterval(interval);
  }, [user, getAuthHeaders]);

  if (loading || !showPopup || !suspensionData?.is_suspended) {
    return null;
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div 
      className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-black/70"
      data-testid="suspension-popup"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldOff className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Account Suspended</h2>
          <p className="text-red-100 text-sm">
            Your provider account has been temporarily suspended
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Suspension Details */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Suspension Reason:</p>
                <p className="text-red-700 text-sm mt-1">
                  {suspensionData.suspension_reason || 'Contact admin for details'}
                </p>
              </div>
            </div>
          </div>

          {/* Suspension Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Clock className="w-4 h-4" />
                <span>Suspended On</span>
              </div>
              <p className="font-medium text-gray-800">
                {formatDate(suspensionData.suspended_at)}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <ShieldOff className="w-4 h-4" />
                <span>Suspended By</span>
              </div>
              <p className="font-medium text-gray-800">
                {suspensionData.suspended_by_name || 'Admin'}
              </p>
            </div>
          </div>

          {/* What's Affected */}
          <div className="border rounded-lg p-4">
            <h3 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-blue-500" />
              What does this mean?
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-red-500">•</span>
                You cannot receive or accept new job offers
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500">•</span>
                Your pending payments require admin approval
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500">•</span>
                You cannot go online until reinstated
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">•</span>
                You can still view your history and earnings
              </li>
            </ul>
          </div>

          {/* Contact Admin */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-800 mb-3">
              Contact Admin to Resolve
            </h3>
            <div className="space-y-2">
              <a 
                href="tel:2602183536"
                className="flex items-center gap-3 p-2 bg-white rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors"
              >
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Call Admin</p>
                  <p className="text-sm text-gray-500">(260) 218-3536</p>
                </div>
              </a>
              <a 
                href="mailto:Pardonmetodeath@gmail.com"
                className="flex items-center gap-3 p-2 bg-white rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">Email Admin</p>
                  <p className="text-sm text-gray-500">Pardonmetodeath@gmail.com</p>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t flex gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={logout}
          >
            Sign Out
          </Button>
          <Button 
            className="flex-1 bg-blue-600 hover:bg-blue-700"
            onClick={() => window.location.href = '/settings'}
          >
            View Account
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * SuspensionWarningBanner - Shows a warning banner for pending suspension
 */
export function SuspensionWarningBanner() {
  const { user, getAuthHeaders } = useAuth();
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    const checkPending = async () => {
      if (!user) return;
      
      try {
        const response = await axios.get(`${API}/user/suspension-status`, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        setIsPending(response.data.is_pending_suspension);
      } catch (error) {
        console.error('Failed to check suspension:', error);
      }
    };

    checkPending();
    const interval = setInterval(checkPending, 60000);
    return () => clearInterval(interval);
  }, [user, getAuthHeaders]);

  if (!isPending) return null;

  return (
    <div className="bg-orange-500 text-white px-4 py-2 text-sm flex items-center justify-center gap-2">
      <AlertTriangle className="w-4 h-4" />
      <span>
        Your account will be suspended after your current job is completed. 
        <a href="tel:2602183536" className="underline ml-1">Contact admin</a>
      </span>
    </div>
  );
}
